import type { FloorPlan, PropertyType, RoadDirection } from '../types';
import { FLOOR_PLANS, ROAD_DIRECTIONS } from '../types';

export interface DeepLinkState {
  position: [number, number];
  propertyType: PropertyType;
  floorPlan: FloorPlan;
  buildingYear: number;
  area: number | undefined;
  floorArea: number | undefined;
  roadWidthM: number | undefined;
  roadDirection: RoadDirection | undefined;
}

const isRoadDirection = (s: string): s is RoadDirection =>
  (ROAD_DIRECTIONS as readonly string[]).includes(s);

const isFloorPlan = (s: string): s is FloorPlan =>
  (FLOOR_PLANS as readonly string[]).includes(s);

const num = (v: string | null): number | undefined => {
  if (v == null || v === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

export function parseDeepLink(search: string): DeepLinkState | null {
  const p = new URLSearchParams(search);
  const lat = num(p.get('lat'));
  const lng = num(p.get('lng'));
  if (lat == null || lng == null) return null;

  const pt = p.get('pt');
  const propertyType: PropertyType = pt === 'house' ? 'house' : 'condo';

  const fpRaw = p.get('fp') ?? '2LDK';
  const floorPlan: FloorPlan = isFloorPlan(fpRaw) ? fpRaw : '2LDK';

  const by = num(p.get('by'));
  const buildingYear = by != null ? Math.round(by) : new Date().getFullYear() - 10;

  const rdRaw = p.get('rd');
  const roadDirection: RoadDirection | undefined =
    rdRaw && isRoadDirection(rdRaw) ? rdRaw : undefined;

  return {
    position: [lat, lng],
    propertyType,
    floorPlan,
    buildingYear,
    area: num(p.get('a')),
    floorArea: num(p.get('fa')),
    roadWidthM: num(p.get('rw')),
    roadDirection,
  };
}

export function buildDeepLinkParams(s: DeepLinkState): URLSearchParams {
  const p = new URLSearchParams();
  p.set('lat', s.position[0].toFixed(6));
  p.set('lng', s.position[1].toFixed(6));
  p.set('pt', s.propertyType);
  p.set('fp', s.floorPlan);
  p.set('by', String(s.buildingYear));
  if (s.area != null) p.set('a', String(s.area));
  if (s.floorArea != null) p.set('fa', String(s.floorArea));
  if (s.roadWidthM != null) p.set('rw', String(s.roadWidthM));
  if (s.roadDirection != null) p.set('rd', s.roadDirection);
  return p;
}

export function buildShareUrl(s: DeepLinkState): string {
  if (typeof window === 'undefined') return '';
  return `${window.location.origin}${window.location.pathname}?${buildDeepLinkParams(s).toString()}`;
}
